import { ENV } from '../config/env';
import { supabase } from '../lib/supabaseClient';

const API_BASE_URL = `${ENV.API_URL}/api`;

export type NotepadOptions = {
  summary?: boolean;
  studyGuide?: boolean;
  faq?: boolean;
  briefingDoc?: boolean;
};

export type NotepadRequest = {
  text: string;
  options: NotepadOptions;
  tone?: 'concise' | 'formal' | 'friendly';
  length?: 'short' | 'medium' | 'long';
  // Backend auth middleware currently expects userId in request body
  userId?: string;
  sources?: Array<{ title?: string; text: string }>;
  includeSources?: boolean;
};

export type NotepadResponse = {
  summary?: string;
  studyGuide?: string;
  faq?: string;
  briefingDoc?: string;
};

export type VideoSlide = {
  title: string;
  bullets: string[];
  narration: string;
  imagePrompt?: string;
};

export type NotepadSessionListItem = {
  id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
};

export type NotepadSession = {
  id: string;
  title: string | null;
  input_text: string;
  options: NotepadOptions;
  tone?: 'concise' | 'formal' | 'friendly' | null;
  length?: 'short' | 'medium' | 'long' | null;
  outputs?: NotepadResponse | null;
  sources?: Array<{ title?: string; text: string } | null> | null;
  includeSources?: boolean;
  created_at: string;
  updated_at: string;
};

const getToken = async (): Promise<string> => {
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.access_token || '';
  } catch (e) {
    console.error('Error getting Supabase auth token:', e);
    return '';
  }
};

export async function generateSlideImages(params: {
  slides: Array<Pick<VideoSlide, 'imagePrompt' | 'title' | 'narration'>>;
}): Promise<{ images: Array<{ url: string } | null> } | { error: string }> {
  if (!Array.isArray(params?.slides) || params.slides.length === 0) {
    throw new Error('slides are required');
  }
  const uid = await getUserId();
  if (!uid) {
    return { error: 'You must be logged in' };
  }
  const url = `${API_BASE_URL}/notepad/video/images?userId=${encodeURIComponent(uid)}`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getToken()}`
      },
      body: JSON.stringify({ ...params, userId: uid })
    });
    if (!response.ok) {
      // Return error but let caller decide to fall back
      const text = await response.text();
      let message = text;
      try { message = JSON.parse(text)?.error || JSON.parse(text)?.message || text; } catch {}
      return { error: `Image generation failed: ${response.status} ${response.statusText}. ${message}` };
    }
    const data = await response.json();
    if (!data || !Array.isArray(data.images)) {
      return { error: 'Invalid images response' };
    }
    return { images: data.images as Array<{ url: string } | null> };
  } catch (err) {
    console.error('Error generating slide images:', err);
    return { error: err instanceof Error ? err.message : 'Unknown error generating images' };
  }
}

export async function uploadSlidesData(params: { dataUrls: string[] }): Promise<{ images: Array<{ url: string }> }> {
  if (!Array.isArray(params?.dataUrls) || params.dataUrls.length === 0) {
    throw new Error('dataUrls are required');
  }
  const uid = await getUserId();
  if (!uid) {
    throw new Error('You must be logged in');
  }
  const url = `${API_BASE_URL}/notepad/video/slides/upload?userId=${encodeURIComponent(uid)}`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getToken()}`
      },
      body: JSON.stringify({ images: params.dataUrls.map(d => ({ dataUrl: d })), userId: uid })
    });
    if (!response.ok) {
      const text = await response.text();
      let message = text;
      try { message = JSON.parse(text)?.error || JSON.parse(text)?.message || text; } catch {}
      throw new Error(`Upload slides failed: ${response.status} ${response.statusText}. ${message}`);
    }
    const data = await response.json();
    if (!data || !Array.isArray(data.images)) {
      throw new Error('Invalid slide upload response');
    }
    return { images: data.images as Array<{ url: string }> };
  } catch (err) {
    console.error('Error uploading slides:', err);
    throw err instanceof Error ? err : new Error('Unknown error uploading slides');
  }
}

export async function renderSlides(params: {
  slides: Array<Pick<VideoSlide, 'title' | 'bullets' | 'narration'>>;
  theme?: { variant?: 'minimal-light'; primaryColor?: string; accentColor?: string; fontFamily?: string };
}): Promise<{ images: Array<{ url: string }> }> {
  if (!Array.isArray(params?.slides) || params.slides.length === 0) {
    throw new Error('slides are required');
  }
  const uid = await getUserId();
  if (!uid) {
    throw new Error('You must be logged in');
  }
  const url = `${API_BASE_URL}/notepad/video/slides/render?userId=${encodeURIComponent(uid)}`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getToken()}`
      },
      body: JSON.stringify({
        slides: params.slides,
        theme: { variant: 'minimal-light', ...(params.theme || {}) },
        userId: uid
      })
    });
    if (!response.ok) {
      const text = await response.text();
      let message = text;
      try { message = JSON.parse(text)?.error || JSON.parse(text)?.message || text; } catch {}
      throw new Error(`Render slides failed: ${response.status} ${response.statusText}. ${message}`);
    }
    const data = await response.json();
    if (!data || !Array.isArray(data.images)) {
      throw new Error('Invalid slide render response');
    }
    return { images: data.images as Array<{ url: string }> };
  } catch (err) {
    console.error('Error rendering slides:', err);
    throw err instanceof Error ? err : new Error('Unknown error rendering slides');
  }
}

export async function checkImagesReady(urls: string[]): Promise<{
  statuses: Array<{ url: string; ready: boolean; status?: number; contentType?: string; contentLength?: number }>;
  allReady: boolean;
  readyCount: number;
  total: number;
}> {
  if (!Array.isArray(urls) || urls.length === 0) {
    throw new Error('urls are required');
  }
  const uid = await getUserId();
  if (!uid) {
    throw new Error('You must be logged in');
  }
  const url = `${API_BASE_URL}/notepad/video/images/ready?userId=${encodeURIComponent(uid)}`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getToken()}`
      },
      body: JSON.stringify({ urls })
    });
    if (!response.ok) {
      const text = await response.text();
      let message = text;
      try { message = JSON.parse(text)?.error || JSON.parse(text)?.message || text; } catch {}
      throw new Error(`Images readiness check failed: ${response.status} ${response.statusText}. ${message}`);
    }
    const data = await response.json();
    if (!data || !Array.isArray(data.statuses)) {
      throw new Error('Invalid readiness response');
    }
    return data as {
      statuses: Array<{ url: string; ready: boolean; status?: number; contentType?: string; contentLength?: number }>;
      allReady: boolean;
      readyCount: number;
      total: number;
    };
  } catch (err) {
    console.error('Error checking images readiness:', err);
    throw err instanceof Error ? err : new Error('Unknown error checking images readiness');
  }
}


export async function createVoiceover(params: {
  slides: VideoSlide[];
  voice?: string;
}): Promise<{ audio: Array<{ url: string; path: string; durationSec?: number }> }> {
  if (!Array.isArray(params?.slides) || params.slides.length === 0) {
    throw new Error('slides are required');
  }
  const uid = await getUserId();
  if (!uid) {
    throw new Error('You must be logged in');
  }
  const url = `${API_BASE_URL}/notepad/video/voiceover?userId=${encodeURIComponent(uid)}`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getToken()}`
      },
      body: JSON.stringify({ ...params, userId: uid })
    });
    if (!response.ok) {
      const text = await response.text();
      let message = text;
      try { message = JSON.parse(text)?.error || JSON.parse(text)?.message || text; } catch {}
      throw new Error(`Create voiceover failed: ${response.status} ${response.statusText}. ${message}`);
    }
    const data = await response.json();
    if (!data || !Array.isArray(data.audio)) {
      throw new Error('Invalid voiceover response');
    }
    return { audio: data.audio as Array<{ url: string; path: string; durationSec?: number }> };
  } catch (err) {
    console.error('Error creating voiceover:', err);
    throw err instanceof Error ? err : new Error('Unknown error creating voiceover');
  }
}

export async function renderVideo(params: {
  slides: VideoSlide[];
  audio: Array<{ url: string; durationSec?: number }>;
  images?: Array<{ url: string } | null>;
}): Promise<{ videoUrl?: string }> {
  if (!Array.isArray(params?.slides) || params.slides.length === 0) {
    throw new Error('slides are required');
  }
  if (!Array.isArray(params?.audio) || params.audio.length !== params.slides.length) {
    throw new Error('audio must match slides length');
  }
  const uid = await getUserId();
  if (!uid) {
    throw new Error('You must be logged in');
  }
  const url = `${API_BASE_URL}/notepad/video/render?userId=${encodeURIComponent(uid)}`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getToken()}`
      },
      body: JSON.stringify({ ...params, userId: uid })
    });
    if (!response.ok) {
      const text = await response.text();
      let message = text;
      try { message = JSON.parse(text)?.error || JSON.parse(text)?.message || text; } catch {}
      throw new Error(`Render video failed: ${response.status} ${response.statusText}. ${message}`);
    }
    const data = await response.json();
    return data as { videoUrl?: string };
  } catch (err) {
    console.error('Error rendering video:', err);
    throw err instanceof Error ? err : new Error('Unknown error rendering video');
  }
}
 

export async function listSessions(limit = 20): Promise<NotepadSessionListItem[]> {
  const userId = await getUserId();
  const url = `${API_BASE_URL}/notepad/sessions?limit=${encodeURIComponent(String(limit))}&userId=${encodeURIComponent(userId)}`;
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${await getToken()}`
      }
    });
    if (!response.ok) {
      const text = await response.text();
      let message = text;
      try { message = JSON.parse(text)?.error || JSON.parse(text)?.message || text; } catch {}
      throw new Error(`List sessions failed: ${response.status} ${response.statusText}. ${message}`);
    }
    const data = await response.json();
    return (data.sessions || []) as NotepadSessionListItem[];
  } catch (err) {
    console.error('Error listing Notepad sessions:', err);
    throw err instanceof Error ? err : new Error('Unknown error listing sessions');
  }
}

export async function getSession(id: string): Promise<NotepadSession> {
  const userId = await getUserId();
  const url = `${API_BASE_URL}/notepad/sessions/${encodeURIComponent(id)}?userId=${encodeURIComponent(userId)}`;
  try {
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${await getToken()}`
      }
    });
    if (!response.ok) {
      const text = await response.text();
      let message = text;
      try { message = JSON.parse(text)?.error || JSON.parse(text)?.message || text; } catch {}
      throw new Error(`Get session failed: ${response.status} ${response.statusText}. ${message}`);
    }
    const data = await response.json();
    return data.session as NotepadSession;
  } catch (err) {
    console.error('Error fetching Notepad session:', err);
    throw err instanceof Error ? err : new Error('Unknown error fetching session');
  }
}

export async function saveSession(payload: {
  title?: string;
  text: string;
  options: NotepadOptions;
  tone?: 'concise' | 'formal' | 'friendly';
  length?: 'short' | 'medium' | 'long';
  outputs?: NotepadResponse;
  sources?: Array<{ title?: string; text: string }>;
  includeSources?: boolean;
}): Promise<{ sessionId: string }>
{
  if (!payload.text || !payload.options) {
    throw new Error('text and options are required');
  }

  const url = `${API_BASE_URL}/notepad/sessions`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getToken()}`
      },
      body: JSON.stringify({ ...payload, userId: await getUserId() })
    });
    if (!response.ok) {
      const text = await response.text();
      let message = text;
      try { message = JSON.parse(text)?.error || JSON.parse(text)?.message || text; } catch {}
      throw new Error(`Save session failed: ${response.status} ${response.statusText}. ${message}`);
    }
    const data = await response.json();
    return { sessionId: data.session?.id };
  } catch (err) {
    console.error('Error saving Notepad session:', err);
    throw err instanceof Error ? err : new Error('Unknown error saving session');
  }
}

const getUserId = async (): Promise<string> => {
  try {
    const { data } = await supabase.auth.getUser();
    return data.user?.id || '';
  } catch (e) {
    console.error('Error getting Supabase user id:', e);
    return '';
  }
};

export async function summarize(req: NotepadRequest): Promise<NotepadResponse> {
  // Validate required params before calling API
  if (!req?.text || typeof req.text !== 'string' || !req.text.trim()) {
    throw new Error('Text is required');
  }
  if (!req.options || typeof req.options !== 'object') {
    throw new Error('Options are required');
  }
  const { summary, studyGuide, faq, briefingDoc } = req.options as any;
  if (!summary && !studyGuide && !faq && !briefingDoc) {
    throw new Error('Select at least one output: Summary, Study Guide, FAQ, or Briefing Document');
  }
  if (req.includeSources && req.sources) {
    for (const s of req.sources) {
      if (!s || typeof s.text !== 'string' || !s.text.trim()) {
        throw new Error('Each source must include text');
      }
    }
  }

  const url = `${API_BASE_URL}/notepad/summarize`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getToken()}`
      },
      body: JSON.stringify({ ...req, userId: req.userId || (await getUserId()) })
    });

    if (!response.ok) {
      const text = await response.text();
      let message = text;
      try { message = JSON.parse(text)?.error || JSON.parse(text)?.message || text; } catch {}
      throw new Error(`Summarization failed: ${response.status} ${response.statusText}. ${message}`);
    }

    const data = await response.json();

    // Basic response validation
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid response from server');
    }

    return data as NotepadResponse;
  } catch (err) {
    console.error('Error calling Notepad summarize:', err);
    throw err instanceof Error ? err : new Error('Unknown error during summarization');
  }
}

export async function generateVideoOutline(params: {
  text: string;
  tone?: 'concise' | 'formal' | 'friendly';
  sources?: Array<{ title?: string; text: string }>;
  includeSources?: boolean;
  maxSlides?: number; // server will cap to 7
}): Promise<{ slides: VideoSlide[] }> {
  if (!params?.text || !params.text.trim()) {
    throw new Error('Text is required');
  }
  const uid = await getUserId();
  if (!uid) {
    throw new Error('You must be logged in to generate a video');
  }
  const body = { ...params, userId: uid };
  const url = `${API_BASE_URL}/notepad/video/outline?userId=${encodeURIComponent(uid)}`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${await getToken()}`
      },
      body: JSON.stringify(body)
    });
    if (!response.ok) {
      const text = await response.text();
      let message = text;
      try { message = JSON.parse(text)?.error || JSON.parse(text)?.message || text; } catch {}
      throw new Error(`Generate outline failed: ${response.status} ${response.statusText}. ${message}`);
    }
    const data = await response.json();
    if (!data || !Array.isArray(data.slides)) {
      throw new Error('Invalid outline response');
    }
    return { slides: data.slides as VideoSlide[] };
  } catch (err) {
    console.error('Error generating video outline:', err);
    throw err instanceof Error ? err : new Error('Unknown error generating outline');
  }
}
